import type { ErrorEvent, StackFrame } from '@sentry/react'

/**
 * Client-side Sentry inbound filters.
 *
 * Kept in the repo rather than configured as a Sentry dashboard inbound filter
 * so the rule is versioned, reviewable, and travels with the code that
 * justifies it.
 */

/**
 * V8's message for a cyclic value handed to `JSON.stringify`. The real event
 * carries a multi-line form that names the offending property path, so this
 * matches the prefix rather than the whole string.
 */
const CIRCULAR_JSON_MESSAGE = /converting circular structure to json/i

/**
 * Filenames Sentry itself treats as non-attributable when resolving an event's
 * origin (see `_getLastValidUrl` in @sentry/core's eventFilters integration).
 * A frame carrying one of these has no source URL, which is what a script
 * evaluated without one — an injected extension content script — looks like.
 */
const NON_ATTRIBUTABLE_FILENAMES = new Set(['<anonymous>', 'anonymous', '[native code]'])

/**
 * Native DOM mutation methods. These only appear as stack frames if something
 * has monkey-patched them; nothing in apps/web does.
 */
const PATCHABLE_DOM_METHODS = /\b(appendChild|insertBefore|replaceChild|append|prepend)\b/

function hasNoResolvableOrigin(frame: StackFrame): boolean {
  const filename = frame.filename
  return !filename || NON_ATTRIBUTABLE_FILENAMES.has(filename)
}

function isPatchedDomMutationFrame(frame: StackFrame): boolean {
  const fn = frame.function
  if (!fn || !PATCHABLE_DOM_METHODS.test(fn)) return false
  // A DOM-mutation frame that resolves to one of our bundles is our own call,
  // not a patch. Only an unattributable one is evidence of injection.
  return hasNoResolvableOrigin(frame)
}

/**
 * KAN-434 / Sentry INTELHUB-WEB-4.
 *
 * A browser extension patches `HTMLElement.appendChild` and JSON-serialises
 * every node passed to it. React's commit phase appends elements carrying a
 * `__reactFiber$` property whose `stateNode` closes a reference cycle, so the
 * serialisation throws. We cannot fix a script we do not ship, the error is
 * caught by our error boundary (`handled = yes`), and `userCount` is 0.
 *
 * The match is deliberately narrow — message AND unattributable frames AND an
 * unattributable DOM-mutation frame — so a genuine circular-JSON bug in our own
 * code still reports. If the extension changes technique the event comes back,
 * which is the correct failure direction: re-triage beats silent over-suppression.
 */
export function isThirdPartyCircularJsonEvent(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values?.length) return false

  return values.some((value) => {
    if (!CIRCULAR_JSON_MESSAGE.test(value.value ?? '')) return false

    const frames = value.stacktrace?.frames
    if (!frames?.length) return false

    return frames.some(hasNoResolvableOrigin) && frames.some(isPatchedDomMutationFrame)
  })
}

/**
 * Sentry `beforeSend`. Returns `null` to drop an event, or the event unchanged.
 *
 * Note this is a `beforeSend` rather than an `ignoreErrors` entry on purpose:
 * `ignoreErrors` matches on the message alone, which would also suppress a real
 * first-party circular-JSON bug. The discriminator here is the stack shape.
 */
export function dropThirdPartyDomPatchNoise(event: ErrorEvent): ErrorEvent | null {
  return isThirdPartyCircularJsonEvent(event) ? null : event
}

/**
 * The exact prefix `@sentry/browser` puts in front of a promise rejected with a
 * PRIMITIVE reason — `_eventFromRejectionWithPrimitive` in its globalhandlers
 * integration. That path also sets `type: 'UnhandledRejection'` and attaches NO
 * stacktrace, so all three properties below are evidence about how the event
 * was manufactured, not just about what it says.
 */
const NON_ERROR_REJECTION_PREFIX = 'Non-Error promise rejection captured with value: '

/**
 * The Microsoft Outlook Safe Links / Office link-scanner signature. ANCHORED on
 * purpose: a first-party error that merely quotes this text while reporting
 * something real must still be delivered. The id and method vary between hits;
 * the shape does not.
 */
const OFFICE_LINK_SCANNER_REASON =
  /^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/

/**
 * KAN-646 / Sentry INTELHUB-WEB-5.
 *
 *   UnhandledRejection: Non-Error promise rejection captured with value:
 *   Object Not Found Matching Id:6, MethodName:update, ParamCount:4
 *
 * on path `/`, Chrome 142 / Windows, **userCount 0**, recurring at 05:31Z — one
 * minute after the weekly briefing send. A headless client opening the links in
 * a mail we just delivered, not a reader.
 *
 * The payload is the long-documented Outlook Safe Links / Office link-scanner
 * signature: the scanner injects a script that calls back into a host bridge
 * that is no longer present, and rejects with that bare string. There is no
 * IntelHub code on either side of it. We do not ship the script, no user is
 * affected, and there is nothing to act on.
 *
 * Narrow by construction, in the same spirit as the KAN-434 rule above:
 *
 *   1. the event carries EXACTLY ONE exception value. Sentry's primitive
 *      -rejection path produces exactly one, so this is evidence about how the
 *      event was built — and it means a chained or aggregate event that
 *      happens to contain this rejection alongside a real first-party
 *      exception is never dropped on the strength of the noise half;
 *   2. `type` is Sentry's synthetic `UnhandledRejection`, which a genuine
 *      thrown Error never has (it carries its constructor name instead);
 *   3. the value carries the non-Error rejection prefix, so the reason really
 *      was a bare primitive rather than an Error we produced;
 *   4. the remaining payload matches the anchored regex EXACTLY.
 *
 * The final `hasNoResolvableOrigin` check is a SAFETY VALVE, not evidence:
 * this event normally carries no stack at all, so an absent stack proves
 * nothing about origin. It exists only so that if anything ever does attach
 * frames of OURS to an event with this text, the event stays attributable and
 * keeps reporting.
 *
 * `Id` and `MethodName` are matched as classes rather than pinned to the one
 * observed hit because the scanner varies them per injection — pinning `Id:6`
 * would simply readmit the same noise under `Id:7`. `ParamCount` likewise. The
 * anchoring is what keeps this narrow: the WHOLE reason must be this string
 * and nothing else. If the scanner changes its string the event comes back,
 * which is the correct failure direction — re-triage beats silent
 * over-suppression.
 */
export function isOfficeLinkScannerRejection(event: ErrorEvent): boolean {
  const values = event.exception?.values
  // Exactly one, not "any of" — see (1) above.
  if (values?.length !== 1) return false

  const value = values[0]
  if (value.type !== 'UnhandledRejection') return false

  const raw = value.value ?? ''
  if (!raw.startsWith(NON_ERROR_REJECTION_PREFIX)) return false
  if (!OFFICE_LINK_SCANNER_REASON.test(raw.slice(NON_ERROR_REJECTION_PREFIX.length))) {
    return false
  }

  const frames = value.stacktrace?.frames
  return !frames?.some((frame) => !hasNoResolvableOrigin(frame))
}

/**
 * The Sentry `beforeSend` that main.tsx actually installs: every third-party
 * noise rule, composed. Each rule stays independently exported and
 * independently tested, so adding one cannot weaken another — and dropping an
 * event still requires a specific rule to claim it.
 */
export function dropThirdPartyNoise(event: ErrorEvent): ErrorEvent | null {
  if (isThirdPartyCircularJsonEvent(event)) return null
  if (isOfficeLinkScannerRejection(event)) return null
  return event
}
